import React from 'react';

import { useGetStream } from '@vlabs/api/artemy/client';
import { StreamTargetsParameterItem } from '@vlabs/api/artemy/model';
import type { StreamId } from '@vlabs/api/artemy/model';
import {
  ActivityIcon, ChevronDoubleRightIcon, RefreshRightIcon, SettingsIcon,
} from '@vlabs/icons';
import { StreamStatus } from '@vlabs/monokit/entities/streams';
import { GoToEditStreamIconButton } from '@vlabs/monokit/features/go-to-edit-stream';
import { PageHeader, usePageContext } from '@vlabs/monokit/shared/ui/page';
import { useSidePanel } from '@vlabs/monokit/shared/ui/side-panel';
import { Breadcrumb, Breadcrumbs, Control } from '@vlabs/uikit';

import { StreamQualitySelect } from './components/StreamQualitySelect';
import { useSourceLivePageTranslation } from './i18n';
import { useRestream } from './restream-context';
import { routes } from '../../app/routes';

interface SourceLivePageHeaderProps {
  streamId: StreamId;
}

// Шапка ретрансляции: самодостаточна — название и статус запрашивает сама,
// переподключение и качество берёт из контекста ретрансляции.
function SourceLivePageHeader({
  streamId,
}: SourceLivePageHeaderProps): React.JSX.Element {
  const { t } = useSourceLivePageTranslation();
  const pageCtx = usePageContext();
  const { isOpen, toggleIsOpen } = useSidePanel();
  const { reconnect } = useRestream();

  const { data: stream } = useGetStream(streamId, {
    targets: [StreamTargetsParameterItem.name],
  });
  const title = stream?.name ?? streamId;

  // Панель закрыта — иконка событий; открыта — шеврон
  let eventsIcon: React.JSX.Element = <ActivityIcon />;
  if (isOpen) {
    eventsIcon = <ChevronDoubleRightIcon />;
  }

  return (
    <PageHeader
      actions={[
        <StreamQualitySelect
          data-testid="stream-quality-select"
          key="quality"
        />,
        <Control.Button
          data-testid="stream-reconnect"
          icon={<RefreshRightIcon />}
          key="connect"
          kind="primary"
          onClick={reconnect}
          size={pageCtx.buttonSize}
        >
          {t('connect')}
        </Control.Button>,
        <GoToEditStreamIconButton
          icon={<SettingsIcon />}
          key="settings"
          kind="primary"
          shape="square"
          streamId={streamId}
          variant="outlined"
        />,
        <Control.Button
          data-testid="stream-toggle-events"
          icon={eventsIcon}
          key="events"
          kind="primary"
          onClick={toggleIsOpen}
          size={pageCtx.buttonSize}
          variant="outlined"
        >
          {t('events')}
        </Control.Button>,
      ]}
      breadcrumbs={(
        <Breadcrumbs>
          <Breadcrumb
            caption={t('breadcrumb_sources')}
            href={routes.sources.list}
          />
          <Breadcrumb caption={title} />
          <Breadcrumb caption={t('translation')} />
        </Breadcrumbs>
      )}
      tag={(
        <StreamStatus
          data-testid="stream-status"
          streamId={streamId}
        />
)}
      title={title}
    />
  );
}

export { SourceLivePageHeader };
